export interface Coordinated {
  lat: number;
  lng: number;
}

export function surpriseMe<T>(pois: T[], rng: () => number = Math.random): T | null {
  if (pois.length === 0) return null;
  const idx = Math.min(pois.length - 1, Math.floor(rng() * pois.length));
  return pois[idx]!;
}

/**
 * Format a distance in meters as a short human-readable string.
 *   847   → "847 m"
 *   1_500 → "1.5 km"
 *   12_000 → "12 km"
 */
export function formatDistanceM(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10_000 ? 1 : 0)} km`;
}

/**
 * "0.5 km from Osaka" when an origin label is given, otherwise "0.5 km away".
 * Centralizes the wording so callers don't disagree on " from " vs " away".
 */
export function formatDistanceFrom(meters: number, originLabel?: string): string {
  return originLabel ? `${formatDistanceM(meters)} from ${originLabel}` : `${formatDistanceM(meters)} away`;
}

/**
 * Default radius for "What's nearby" lookups, in kilometers.
 * 1.5 km ≈ 18 min walk — the upper end of the "15-minute city" band, which
 * tourism walking studies suggest matches travelers' tolerance better than
 * the daily-errand 800m–1km threshold.
 */
export const DEFAULT_NEARBY_RADIUS_KM = 1.5;

const EARTH_RADIUS_M = 6_371_000;

export function haversineDistanceM(a: Coordinated, b: Coordinated): number {
  const φ1 = (a.lat * Math.PI) / 180;
  const φ2 = (b.lat * Math.PI) / 180;
  const Δφ = ((b.lat - a.lat) * Math.PI) / 180;
  const Δλ = ((b.lng - a.lng) * Math.PI) / 180;
  const x = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return EARTH_RADIUS_M * c;
}

export function centroid(pois: Coordinated[]): Coordinated | null {
  if (pois.length === 0) return null;
  let lat = 0;
  let lng = 0;
  for (const p of pois) {
    lat += p.lat;
    lng += p.lng;
  }
  return { lat: lat / pois.length, lng: lng / pois.length };
}

/**
 * Pure: filter `pois` to those within `radiusKm` of `source`, sorted by distance ascending.
 * Optional `filter` lets the caller exclude e.g. the source POI itself or POIs already in
 * a particular group.
 */
export function nearThis<T extends Coordinated>(
  source: Coordinated,
  pois: T[],
  radiusKm: number,
  filter?: (p: T) => boolean,
): { poi: T; distanceM: number }[] {
  const radiusM = radiusKm * 1000;
  const out: { poi: T; distanceM: number }[] = [];
  for (const p of pois) {
    if (filter && !filter(p)) continue;
    const distanceM = haversineDistanceM(source, p);
    if (distanceM <= radiusM) out.push({ poi: p, distanceM });
  }
  out.sort((a, b) => a.distanceM - b.distanceM);
  return out;
}

/**
 * Pure: like nearThis but accepts multiple source coordinates. A POI is included if it
 * sits within radiusKm of *any* source. Each result's distance is the minimum to any
 * source. Used for "What's nearby this group" — a multi-city group's centroid is
 * meaningless (lands in the ocean between cities), so we ask "near any POI in this
 * group" instead.
 */
export function nearAnyOf<T extends Coordinated>(
  sources: Coordinated[],
  pois: T[],
  radiusKm: number,
  filter?: (p: T) => boolean,
): { poi: T; distanceM: number }[] {
  if (sources.length === 0) return [];
  const radiusM = radiusKm * 1000;
  const out: { poi: T; distanceM: number }[] = [];
  for (const p of pois) {
    if (filter && !filter(p)) continue;
    let minD = Infinity;
    for (const s of sources) {
      const d = haversineDistanceM(s, p);
      if (d < minD) minD = d;
    }
    if (minD <= radiusM) out.push({ poi: p, distanceM: minD });
  }
  out.sort((a, b) => a.distanceM - b.distanceM);
  return out;
}

export function closestToMe<T extends Coordinated>(
  pois: T[],
  origin: Coordinated,
): { poi: T; distanceM: number } | null {
  if (pois.length === 0) return null;
  let best = { poi: pois[0]!, distanceM: haversineDistanceM(origin, pois[0]!) };
  for (let i = 1; i < pois.length; i++) {
    const d = haversineDistanceM(origin, pois[i]!);
    if (d < best.distanceM) best = { poi: pois[i]!, distanceM: d };
  }
  return best;
}
